/**
 * Canonical verifier for "did an admin grant this check?" flags.
 *
 * Background: verification status is stored as a free-text string on the profile document,
 * and the writers are not uniform across the platform's history. The current admin
 * verification action (`src/app/admin/verifications/actions.ts`) writes exactly three
 * values — `'Verified'` on approval, `'Failed'` on rejection, `'No'` on revoke — while
 * older/other paths and the `BADGE_MAP` labels ('ID Verified', 'CIPC Registered',
 * 'Tax Compliance Status (TCS)', 'Plumbing COC Issuer', 'SARS Practitioner', …) are also
 * seen in the field.
 *
 * Readers across the codebase compared against exactly `'verified'`, so any value outside
 * that single token rendered as unverified on the public profile, on the dashboard and on
 * the badge. Read every status through this helper instead of string-comparing it.
 *
 * The substring match is safe against the negative states: 'Failed', 'No', 'Pending Review'
 * and 'Submitted' contain none of the affirmative tokens below, and are additionally
 * denied by NEGATIVE_STATUSES.
 */

/** Statuses that mean "received but not yet granted" — neither verified nor failed. */
const PENDING_STATUSES = new Set([
  'pending',
  'pending review',
  'in review',
  'in process',
  'submitted',
  'under review',
]);

/** Statuses that mean "rejected / not granted" and must never be treated as verified. */
const NEGATIVE_STATUSES = new Set([
  'not verified',
  'unverified',
  'rejected',
  'declined',
  'failed',
  'expired',
  'invalid',
  'pending',
  'pending review',
  'in review',
  'submitted',
  'none',
  'n/a',
  'na',
]);

/**
 * A status counts as verified when it is not explicitly negative and either
 * contains "verified"/"registered"/"certified" or is an affirmative token.
 */
export function isVerificationStatusVerified(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;
  if (NEGATIVE_STATUSES.has(normalized)) return false;
  if (normalized === 'verified' || normalized === 'yes' || normalized === 'true') return true;
  if (/^not\s/.test(normalized) || /^un/.test(normalized) || /^pending/.test(normalized)) return false;
  return (
    normalized.includes('verified') ||
    normalized.includes('registered') ||
    normalized.includes('certified') ||
    normalized.includes('compliant') ||
    normalized.includes('compliance') ||
    normalized.includes('insured') ||
    normalized.includes('licensed') ||
    // BADGE_MAP-adjacent grant labels that carry no verified/registered/certified token.
    normalized.includes('issuer') ||
    normalized.includes('practitioner')
  );
}

/**
 * A status counts as pending (in review) when it is not verified but carries a
 * received-not-yet-granted token. Used so the dashboard can show "In review"
 * instead of falsely marking a submitted verification as complete.
 */
export function isVerificationStatusPending(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;
  if (isVerificationStatusVerified(normalized)) return false;
  return PENDING_STATUSES.has(normalized) || /^pending/.test(normalized) || /^under\s/.test(normalized);
}